import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * O botão de olho: esconder os níveis na tela de quem JÁ PODE vê-los.
 *
 * ⚠️ Isto NÃO é permissão, e confundir as duas coisas seria grave. O gate de
 * verdade é `canViewRatings`, aplicado no servidor, que decide se o campo
 * `level` sequer sai do banco na resposta (§3). Aqui é apresentação: o
 * organizador está com o celular na roda, ou projetando a tela, e quer mostrar
 * o sorteio sem que o grupo leia a nota de cada um.
 *
 * Por isso a preferência é do APARELHO, e não da conta: ela responde "quem está
 * olhando esta tela agora", que muda de um minuto para o outro e não tem nada a
 * ver com quem fez login. É o mesmo desenho do mobile, que guarda
 * `@balancesquad:hideRatings` no AsyncStorage.
 */

const STORAGE_KEY = 'bs:hideRatings';

function getStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

interface RatingsVisibilityValue {
  hidden: boolean;
  toggle: () => void;
}

const RatingsVisibilityContext = createContext<RatingsVisibilityValue | null>(null);

export function RatingsVisibilityProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState<boolean>(getStored);

  const toggle = useCallback(() => {
    setHidden((previous) => {
      const next = !previous;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Sem persistência disponível; a escolha vale para a sessão atual.
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ hidden, toggle }), [hidden, toggle]);

  return <RatingsVisibilityContext value={value}>{children}</RatingsVisibilityContext>;
}

export function useRatingsVisibility(): RatingsVisibilityValue {
  const ctx = useContext(RatingsVisibilityContext);
  if (!ctx) throw new Error('useRatingsVisibility precisa estar dentro de <RatingsVisibilityProvider>');
  return ctx;
}

/**
 * O nível que a tela deve exibir, ou `undefined` para não exibir nada.
 *
 * Existe para que cada tela não repita `hidden ? undefined : player.level` e
 * uma delas esqueça — o defeito silencioso aqui é o nível continuar aparecendo
 * justamente quando alguém pediu para escondê-lo.
 */
export function useVisibleLevel(level: number | undefined): number | undefined {
  const { hidden } = useRatingsVisibility();
  return hidden ? undefined : level;
}
